import {
  isThisISOWeek,
  isThisYear,
  isToday,
  isTomorrow,
  isYesterday,
} from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GiCancel, GiConfirmed } from 'react-icons/gi';
import { MdEdit, MdOutlineNewReleases } from 'react-icons/md';
import { TbCalendarQuestion } from 'react-icons/tb';
import { useSelector } from 'react-redux';
import { ROUTES } from '../../../constants/Constants';
import useAppNavigation from '../../../hooks/useAppNavigation';
import useTimezone from '../../../hooks/useTimezone';
import useUser from '../../../hooks/useUser';
import {
  ChatMessageType,
  ConversationStatusType,
} from '../../../proxies/ChatProxy';
import type {
  ConversationMessageDataType,
  ConversationUserType,
} from '../../../redux/ducks/ChatDucks.types';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import Button from '../../button/Button';

const ALLOWED_SUMMARY_STATUSES: ChatMessageType[] = [
  ChatMessageType.AppointmentConfirmed,
];

const AppointmentSummary = () => {
  const { t, i18n } = useTranslation();
  const { currentTimezone } = useTimezone();
  const { currentUser } = useUser();
  const { navigate, toLocalizedRoute } = useAppNavigation();

  const { messages, getConversationMessagesResponse } = useSelector(
    (state: RootReducerType) => state.chat,
  );

  const [currentStatusMessage, setCurrentStatusMessage] =
    useState<ConversationMessageDataType | null>();

  const statusMessage = useMemo<ConversationMessageDataType | undefined>(() => {
    if (messages?.status) {
      return messages?.status.data;
    }

    return undefined;
  }, [messages]);

  const conversationUser = useMemo<ConversationUserType | undefined>(() => {
    if (
      getConversationMessagesResponse?.data?.users &&
      getConversationMessagesResponse?.data?.users.length > 0
    ) {
      return getConversationMessagesResponse?.data?.users[0];
    }

    return undefined;
  }, [getConversationMessagesResponse?.data]);

  const mine = useMemo<boolean>(() => {
    if (getConversationMessagesResponse?.data?.status_message && currentUser) {
      return (
        getConversationMessagesResponse.data.status_message?.user_id ===
        currentUser.id
      );
    }

    return false;
  }, [getConversationMessagesResponse?.data?.status_message, currentUser]);

  useEffect(() => {
    if (statusMessage) {
      if (!currentStatusMessage) {
        setCurrentStatusMessage(statusMessage);
      } else if (currentStatusMessage !== statusMessage) {
        setCurrentStatusMessage(statusMessage);
      }
    } else {
      setCurrentStatusMessage(undefined);
    }
  }, [statusMessage]);

  const propBasedStyle = useMemo(() => {
    let result = 'bg-slate-100';
    if (currentStatusMessage) {
      if (
        currentStatusMessage.type === ChatMessageType.AppointmentCancelRequest
      ) {
        result = 'bg-red-100';
      } else if (
        currentStatusMessage.type === ChatMessageType.AppointmentChangeRequest
      ) {
        result = 'bg-yellow-100';
      } else if (
        currentStatusMessage.type === ChatMessageType.AppointmentChanged ||
        currentStatusMessage.type === ChatMessageType.AppointmentConfirmed
      ) {
        result = 'bg-green-100';
      }
    }

    return result;
  }, [currentStatusMessage]);

  const formatDateLabel = useCallback((ts: number) => {
    const messageDate = new Date(ts);

    let datePart = null;

    if (isToday(messageDate)) {
      datePart = t('static.generic.today');
    } else if (isTomorrow(messageDate)) {
      datePart = t('static.generic.tomorrow');
    } else if (isYesterday(messageDate)) {
      datePart = t('static.generic.yesterday');
    } else if (isThisISOWeek(messageDate)) {
      datePart = t('static.generic.this_week_day', {
        name: Intl.DateTimeFormat(i18n.language, {
          weekday: 'long',
          timeZone: currentTimezone,
        }).format(messageDate),
      });
    } else if (isThisYear(messageDate)) {
      datePart = Intl.DateTimeFormat(i18n.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: currentTimezone,
      }).format(new Date(messageDate));
    } else {
      datePart = Intl.DateTimeFormat(i18n.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: currentTimezone,
      }).format(new Date(messageDate));
    }

    if (datePart !== null) {
      return `${datePart}, ${Intl.DateTimeFormat(i18n.language, {
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
        timeZone: currentTimezone,
      }).format(new Date(messageDate))}`;
    }

    return Intl.DateTimeFormat(i18n.language, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
      timeZone: currentTimezone,
    }).format(new Date(messageDate));
  }, []);

  const getMessageHeading = useMemo<string>(() => {
    if (currentStatusMessage) {
      if (currentStatusMessage.type === ChatMessageType.NewAppointment) {
        return mine
          ? t('static.chat.message_header_appointment_new_mine', {
              name: `${conversationUser?.first_name} ${conversationUser?.last_name}`,
            })
          : t('static.chat.message_header_appointment_new_other', {
              name: `${conversationUser?.first_name} ${conversationUser?.last_name}`,
            });
      }

      if (
        currentStatusMessage.type === ChatMessageType.AppointmentChangeRequest
      ) {
        return mine
          ? t('static.chat.message_header_appointment_change_request_mine', {
              name: `${conversationUser?.first_name} ${conversationUser?.last_name}`,
            })
          : t('static.chat.message_header_appointment_change_request_other', {
              name: `${conversationUser?.first_name} ${conversationUser?.last_name}`,
            });
      }

      if (
        currentStatusMessage.type === ChatMessageType.AppointmentCancelRequest
      ) {
        return mine
          ? t('static.chat.message_header_appointment_cancel_request_mine', {
              name: `${conversationUser?.first_name} ${conversationUser?.last_name}`,
            })
          : t('static.chat.message_header_appointment_cancel_request_other', {
              name: `${conversationUser?.first_name} ${conversationUser?.last_name}`,
            });
      }

      if (currentStatusMessage.type === ChatMessageType.AppointmentChanged) {
        return mine
          ? t('static.chat.message_header_appointment_changed_mine', {
              name: `${conversationUser?.first_name} ${conversationUser?.last_name}`,
            })
          : t('static.chat.message_header_appointment_changed_other', {
              name: `${conversationUser?.first_name} ${conversationUser?.last_name}`,
            });
      }

      if (currentStatusMessage.type === ChatMessageType.AppointmentConfirmed) {
        return mine
          ? t('static.chat.message_header_appointment_confirmed_mine', {
              name: `${conversationUser?.first_name} ${conversationUser?.last_name}`,
            })
          : t('static.chat.message_header_appointment_confirmed_other', {
              name: `${conversationUser?.first_name} ${conversationUser?.last_name}`,
            });
      }
    }

    return '';
  }, [mine, conversationUser, currentStatusMessage]);

  const onAppointmentDetailsClick = useCallback(() => {
    if (currentStatusMessage?.meta?.current_appointment) {
      navigate(
        toLocalizedRoute(
          ROUTES.APPOINTMENT_DETAILS_ROUTE.replace(
            ':appointmentId',
            `${currentStatusMessage?.meta?.current_appointment}`,
          ),
        ),
      );
    }
  }, [currentStatusMessage]);

  return statusMessage?.meta &&
    ALLOWED_SUMMARY_STATUSES.includes(statusMessage.type) ? (
    <button
      data-test-id="chat-conversation-status-summary"
      id="chat-conversation-status-summary"
      key="chat-conversation-status-summary"
      aria-label="chat-conversation-status-summary"
      data-umami-event="chat-conversation-status-summary"
      className={`group flex size-full flex-col items-center justify-center border-b border-solid border-slate-300 p-2 text-center shadow-sm hover:shadow-md ${propBasedStyle} cursor-pointer`}
      onClick={onAppointmentDetailsClick}
      type="button"
    >
      <div className="flex size-full flex-row items-center justify-between gap-3">
        <span className="flex min-h-full flex-col items-start justify-center text-lg font-bold">
          {statusMessage.type === ChatMessageType.NewAppointment && (
            <TbCalendarQuestion size={32} className="block" />
          )}
          {statusMessage.type === ChatMessageType.AppointmentChangeRequest && (
            <MdOutlineNewReleases size={32} className="block" />
          )}
          {(statusMessage.type === ChatMessageType.AppointmentConfirmed ||
            statusMessage.type === ChatMessageType.AppointmentChanged) && (
            <GiConfirmed size={32} className="block" />
          )}
          {statusMessage.type === ChatMessageType.AppointmentCancelRequest && (
            <GiCancel size={32} className="block" />
          )}
        </span>
        <span className="flex grow flex-col items-start justify-start text-left text-sm font-semibold leading-tight">
          <span className="mb-1 line-clamp-1 overflow-hidden text-ellipsis break-all font-bold">
            {getMessageHeading}
          </span>
          <span className="line-clamp-1 overflow-hidden text-ellipsis break-all capitalize">
            {statusMessage.meta?.current_status ===
              ConversationStatusType.ChangeRequest &&
              statusMessage.meta?.change_request_service && (
                <span className="font-bold">
                  {statusMessage.meta?.change_request_service.name} (
                  {new Intl.NumberFormat(i18n.language, {
                    style: 'currency',
                    currency:
                      statusMessage.meta?.change_request_service.currency,
                  }).format(statusMessage.meta?.change_request_service.price)}
                  )
                </span>
              )}
            <span
              className={`${
                statusMessage.meta?.current_status ===
                  ConversationStatusType.ChangeRequest &&
                statusMessage.meta?.change_request_service
                  ? 'ml-1 text-slate-500 line-through'
                  : ''
              }`}
            >
              {statusMessage.meta.current_service.name} (
              {new Intl.NumberFormat(i18n.language, {
                style: 'currency',
                currency: statusMessage.meta.current_service.currency,
              }).format(statusMessage.meta.current_service.price)}
              )
            </span>
          </span>
          <span className="line-clamp-1 overflow-hidden text-ellipsis break-all">
            {statusMessage.meta.current_status ===
              ConversationStatusType.ChangeRequest &&
              statusMessage.meta?.change_request_schedule && (
                <span className="font-bold capitalize">
                  {formatDateLabel(statusMessage.meta.change_request_schedule)}
                </span>
              )}
            <span
              className={`capitalize ${
                statusMessage.meta.current_status ===
                  ConversationStatusType.ChangeRequest &&
                statusMessage.meta?.change_request_schedule
                  ? 'ml-1 text-slate-500 line-through'
                  : ''
              }`}
            >
              {formatDateLabel(statusMessage.meta.current_schedule)}
            </span>
          </span>
          <span className="line-clamp-1 overflow-hidden text-ellipsis break-all capitalize">
            {statusMessage.meta.current_status ===
              ConversationStatusType.ChangeRequest &&
              statusMessage.meta.change_request_business && (
                <span className="font-bold">
                  {`${statusMessage.meta.change_request_business.name}`}
                </span>
              )}
            {statusMessage.meta?.current_address?.address && (
              <span
                className={`${
                  statusMessage.meta.current_status ===
                    ConversationStatusType.ChangeRequest &&
                  statusMessage.meta.change_request_business
                    ? 'ml-1 text-slate-500 line-through'
                    : ''
                }`}
              >
                {statusMessage.meta?.current_business?.name
                  ? `${statusMessage.meta.current_business.name}, ${statusMessage.meta.current_address.address}`
                  : statusMessage.meta.current_address.address}
              </span>
            )}
          </span>
        </span>
        <span className="hidden flex-row gap-3">
          <span className="block">
            <Button
              disabled={false}
              loading={false}
              label=""
              icon={MdEdit}
              onClick={() => console.log('On chat appointment edit click')}
              data-test-id="chat-message-summary-action"
              id="chat-message-summary-action"
              outline
            />
          </span>
          <span className="hidden">
            <Button
              disabled={false}
              loading={false}
              label=""
              icon={GiCancel}
              onClick={() => console.log('On chat summary reject click')}
              data-test-id="chat-message-summary-action"
              id="chat-message-summary-action-reject"
              reject
            />
          </span>
          <span className="block">
            <Button
              disabled={false}
              loading={false}
              label=""
              icon={GiConfirmed}
              onClick={() => console.log('On chat summary accept click')}
              data-test-id="chat-message-summary-action"
              id="chat-message-summary-action-accept"
              accept
            />
          </span>
        </span>
      </div>
    </button>
  ) : null;
};

export default AppointmentSummary;
